import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { MonetaryValue } from '@/lib/workflow-defs'; // TODO : replace with GraphQL type

interface CurrencyValueProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: MonetaryValue;
  precision?: number;
  showSymbol?: boolean;
  symbolPosition?: 'before' | 'after';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'muted' | 'success' | 'warning' | 'destructive';
  asChild?: boolean;
}

const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'C$',
  AUD: 'A$',
  BTC: '₿',
  ETH: 'Ξ',
  LTC: 'Ł',
  XRP: 'XRP',
  ADA: '₳',
  DOT: '●',
  LINK: '⬡',
};

const MonetaryValueDisplay = React.forwardRef<
  HTMLSpanElement,
  CurrencyValueProps
>(
  (
    {
      value,
      precision,
      showSymbol = false,
      symbolPosition = 'before',
      size = 'sm',
      variant = 'default',
      className,
      asChild = false,
      style,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'span';
    const numericAmount =
      typeof value.amount === 'string'
        ? Number.parseFloat(value.amount)
        : value.amount;

    const defaultPrecision = () => {
      if (precision !== undefined) return precision;
      const cryptoCurrencies = [
        'BTC',
        'ETH',
        'LTC',
        'XRP',
        'ADA',
        'DOT',
        'LINK',
      ];
      return cryptoCurrencies.includes(value.currency.toUpperCase())
        ? numericAmount < 1
          ? 8
          : 4
        : 2;
    };

    const formattedAmount = numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: defaultPrecision(),
      maximumFractionDigits: defaultPrecision(),
    });
    const symbol = currencySymbols[value.currency.toUpperCase()];
    const displaySymbol = showSymbol && symbol;
    const displayCurrency = value.currency.toUpperCase();

    // CSS-in-JS styles
    const baseStyles: React.CSSProperties = {
      fontFamily:
        'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontVariantNumeric: 'tabular-nums',
      display: 'inline-flex',
      alignItems: 'center',
    };

    const sizeStyles = {
      xs: { fontSize: '0.75rem' },    // 12px
      sm: { fontSize: '0.875rem' },   // 14px
      md: { fontSize: '1rem' },       // 16px
      lg: { fontSize: '1.125rem' },   // 18px
      xl: { fontSize: '1.25rem', fontWeight: '600' }, // 20px
    };

    const variantStyles = {
      default: { color: 'var(--gray-12, #1a1a1a)' },
      muted: { color: 'var(--gray-11, #6e6e6e)' },
      success: { color: 'var(--green-11, #16a34a)' },
      warning: { color: 'var(--amber-11, #d97706)' },
      destructive: { color: 'var(--red-11, #dc2626)' },
    };

    const combinedStyles = {
      ...baseStyles,
      ...sizeStyles[size],
      ...variantStyles[variant],
      ...style,
    };

    return (
      <Comp className={className} style={combinedStyles} ref={ref} {...props}>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {displaySymbol && symbolPosition === 'before' && (
            <span style={{ marginRight: '0.25rem' }}>{symbol}</span>
          )}
          <span>{formattedAmount}</span>
          {displaySymbol && symbolPosition === 'after' && (
            <span style={{ marginLeft: '0.25rem' }}>{symbol}</span>
          )}
          {!displaySymbol && (
            <span
              style={{
                marginLeft: '0.25rem',
                color: 'var(--gray-11, #6e6e6e)',
              }}
            >
              {displayCurrency}
            </span>
          )}
        </span>
      </Comp>
    );
  },
);

export default MonetaryValueDisplay;
